<template>
  <div id="smart-edit-container" :class="{ 'has-expanded-textarea': isTextareaExpanded }">
    <div class="textarea-container" :class="{ 'expanded': isTextareaExpanded }">
      <textarea 
        v-model="localPrompt"
        id="smart-edit-content" 
        class="smart-press-textarea focus:!outline-none focus:!ring-0" 
        placeholder="Write an article about Elon Musk, make sure it's SEO Optimized for the keyword DOGE"
        v-if="!isGenerating && !showResult"
      ></textarea>
      
      <!-- Add copy button next to expand button -->
      <div class="textarea-controls">
        <button 
          class="control-btn relative tooltip tooltip-left" 
          data-tip="Copy to clipboard"
          @click.prevent="copyToClipboard(localPrompt, 'prompt')"
          v-if="!isGenerating && !showResult"
        >
          <span v-if="showPromptCopiedMessage" class="copied-message">Copied!</span>
          <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke-width="1.5" stroke="currentColor" class="size-4">
            <path stroke-linecap="round" stroke-linejoin="round" d="M9 12h3.75M9 15h3.75M9 18h3.75m3 .75H18a2.25 2.25 0 0 0 2.25-2.25V6.108c0-1.135-.845-2.098-1.976-2.192a48.424 48.424 0 0 0-1.123-.08m-5.801 0c-.065.21-.1.433-.1.664 0 .414.336.75.75.75h4.5a.75.75 0 0 0 .75-.75 2.25 2.25 0 0 0-.1-.664m-5.8 0A2.251 2.251 0 0 1 13.5 2.25H15c1.012 0 1.867.668 2.15 1.586m-5.8 0c-.376.023-.75.05-1.124.08C9.095 4.01 8.25 4.973 8.25 6.108V8.25m0 0H4.875c-.621 0-1.125.504-1.125 1.125v11.25c0 .621.504 1.125 1.125 1.125h9.75c.621 0 1.125-.504 1.125-1.125V9.375c0-.621-.504-1.125-1.125-1.125H8.25ZM6.75 12h.008v.008H6.75V12Zm0 3h.008v.008H6.75V15Zm0 3h.008v.008H6.75V18Z" />
          </svg>
        </button>
        <button 
          class="control-btn tooltip tooltip-left" 
          :data-tip="isTextareaExpanded ? 'Collapse' : 'Expand'"
          @click.prevent="toggleTextareaExpand" 
          v-if="!isGenerating && !showResult"
          :title="isTextareaExpanded ? 'Collapse' : 'Expand'"
        >
          <svg v-if="!isTextareaExpanded" xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
            <path stroke-linecap="round" stroke-linejoin="round" d="M3.75 3.75v4.5m0-4.5h4.5m-4.5 0L9 9M3.75 20.25v-4.5m0 4.5h4.5m-4.5 0L9 15M20.25 3.75h-4.5m4.5 0v4.5m0-4.5L15 9m5.25 11.25h-4.5m4.5 0v-4.5m0 4.5L15 15" />
          </svg>

         
          <svg v-else xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
            <path stroke-linecap="round" stroke-linejoin="round" d="M9 9V4.5M9 9H4.5M9 9 3.75 3.75M9 15v4.5M9 15H4.5M9 15l-5.25 5.25M15 9h4.5M15 9V4.5M15 9l5.25-5.25M15 15h4.5M15 15v4.5m0-4.5 5.25 5.25" />         
          </svg>
        </button>
      </div>
    </div>

    <div v-if="isGenerating" class="smart-generating-content">
      <!-- <DotLottieVue style="height: 200px; width: 200px;" autoplay loop src="../wp-content/plugins/smartpress/assets/loading.lottie" /> -->
      
      <!-- Loading Skeleton -->
      <div class="skeleton-container flex flex-col gap-3 mb-4 mt-4">
        <transition-group name="skeleton-fade">
          <div v-for="(skeleton, index) in skeletonElements" :key="index" 
               v-show="skeleton.visible"
               class="skeleton skeleton-item" 
               :class="[skeleton.height, skeleton.width, skeleton.rounded ? 'rounded-md' : '']">
          </div>
        </transition-group>
      </div>
      
      <span class="text-xs text-gray-500">Generating content</span>
      <span class="loading loading-dots loading-sm"></span>
    </div>
    
    <div class="flex justify-between !mt-2" v-if="!showResult && !isGenerating">
      <!-- Templates Button -->
       <div class="flex gap-1">
          <a href="#" 
            class="!btn !btn-sm !bg-secondary !text-white !text-xs !rounded-md !no-underline !font-normal !hover:scale-105 !transition-all !duration-300 !ease-in-out !reset focus:!outline-none focus:!ring-0" 
            :class="{'active': showTemplatesPanel}"
            @click.prevent="$emit('toggle-templates')">
            <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke-width="1.5" stroke="currentColor" class="size-4">
            <path stroke-linecap="round" stroke-linejoin="round" d="M6 6.878V6a2.25 2.25 0 0 1 2.25-2.25h7.5A2.25 2.25 0 0 1 18 6v.878m-12 0c.235-.083.487-.128.75-.128h10.5c.263 0 .515.045.75.128m-12 0A2.25 2.25 0 0 0 4.5 9v.878m13.5-3A2.25 2.25 0 0 1 19.5 9v.878m0 0a2.246 2.246 0 0 0-.75-.128H5.25c-.263 0-.515.045-.75.128m15 0A2.25 2.25 0 0 1 21 12v6a2.25 2.25 0 0 1-2.25 2.25H5.25A2.25 2.25 0 0 1 3 18v-6c0-.98.626-1.813 1.5-2.122" />
            </svg>
            </a>
            <!-- History Button -->
          <div class="!dropdown">
            <a href="#" 
              class="!btn !btn-sm !bg-secondary !text-white !text-xs !rounded-md !no-underline !font-normal !hover:scale-105 !transition-all !duration-300 !ease-in-out !reset focus:!outline-none focus:!ring-0" 
              :class="{'active': showHistoryPanel}"
              @click.prevent="$emit('toggle-history')">
              <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke-width="1.5" stroke="currentColor" class="size-4">
                <path stroke-linecap="round" stroke-linejoin="round" d="M12 6v6h4.5m4.5 0a9 9 0 1 1-18 0 9 9 0 0 1 18 0Z" />
              </svg>
            </a>
            <ul tabindex="0" class="dropdown-content menu bg-base-100 !rounded-box z-1 !w-52 !p-1 !shadow-sm">
              <li class="!text-xs hover:bg-secondary">
                <a 
                  class="!text-black hover:!text-white flex justify-between items-center"
                  @click.prevent="toggleHistoryType();"
                >
                  <span>{{ historyType === 'prompts' ? 'Prompt History' : 'Content History' }}</span>
                  <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke-width="1.5" stroke="currentColor" class="size-4">
                    <path stroke-linecap="round" stroke-linejoin="round" d="M7.5 21L3 16.5m0 0L7.5 12M3 16.5h13.5m0-13.5L21 7.5m0 0L16.5 12M21 7.5H7.5" />
                  </svg>
                </a>
              </li>
              <li class="!text-xs p-2">
                <div class="flex flex-col gap-1 hover:!bg-base-200">
                  <span class="text-black mb-1">Limit History to:</span>
                  <div class="flex gap-2 justify-between">
                    <button 
                      @click.prevent="setHistoryMaxLength(10)" 
                      class="!btn !btn-xs flex-1 !min-h-8 !h-8"
                      :class="historyMaxLength === 10 ? '!bg-secondary !text-white' : '!bg-base-200 !text-black'">
                      10
                    </button>
                    <button 
                      @click.prevent="setHistoryMaxLength(20)" 
                      class="!btn !btn-xs flex-1 !min-h-8 !h-8"
                      :class="historyMaxLength === 20 ? '!bg-secondary !text-white' : '!bg-base-200 !text-black'">
                      20
                    </button>
                    <button 
                      @click.prevent="setHistoryMaxLength(50)" 
                      class="!btn !btn-xs flex-1 !min-h-8 !h-8"
                      :class="historyMaxLength === 50 ? '!bg-secondary !text-white' : '!bg-base-200 !text-black'">
                      50
                    </button>
                  </div>
                </div>
              </li>
            </ul>
          </div>
      </div>
        <!-- Generate Button -->
         <div class="flex gap-1">
          <a href="#" 
            class="!tooltip !tooltip-left tooltip-primary !btn !btn-sm !border-none !bg-primary !opacity-50 !text-white !text-xs !rounded-md !no-underline !font-normal hover:!scale-105 hover:!opacity-100 !transition-all !duration-100 !ease-in-out !reset focus:!outline-none focus:!ring-0" 
            :class="{'active': showTemplatesPanel, 'btn-disabled': isEnhancing, 'animate-pulse': isEnhancing}"
            data-tip="Enhance Prompt"
            @click="enhancePrompt">
            <span v-if="!isEnhancing">
              <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke-width="1.5" stroke="currentColor" class="size-4">
                <path stroke-linecap="round" stroke-linejoin="round" d="m3.75 13.5 10.5-11.25L12 10.5h8.25L9.75 21.75 12 13.5H3.75Z" />
              </svg>
            </span>
            <span v-else class="flex items-center gap-1">
              <svg class="animate-spin size-3" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                <circle class="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" stroke-width="4"></circle>
                <path class="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
              </svg>
              Enhancing
            </span>
            </a>
            <a href="#" 
              class="smart-press-button flex justify-center items-center" 
              :disabled="isGenerating"
              @click.prevent="generateContent">
              {{ isGenerating ? 'Generating content...' : 'Generate' }}
              </a>
         </div>
        
    </div>
    
    <!-- Footer Dropdowns -->
    <div class="flex justify-between">
      <div v-if="!showResult && !isGenerating" class="!mt-2">
        <!-- Output Rule -->
        <div class="!dropdown !cursor-pointer !hover:scale-105 !transition-all !duration-300 !ease-in-out !tooltip !tooltip-left !tooltip-secondary" data-tip="Output rule">
          <div tabindex="0" role="button" class="m-0 p-0 smart-detected">
            <span class="circle" :class="localInputType === 'text' ? '' : '!bg-primary'"></span> <b>{{ localInputType }}</b> Output
            <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke-width="1.5" stroke="currentColor" class="size-3 inline-block ml-1">
              <path stroke-linecap="round" stroke-linejoin="round" d="m19.5 8.25-7.5 7.5-7.5-7.5" />
            </svg>
          </div>
          <ul tabindex="0" class="dropdown-content menu bg-base-100 rounded-box z-1 w-52 !p-4 shadow-sm">
            
            <div class="flex flex-wrap gap-2">
              <button 
                v-for="type in inputTypes" 
                :key="type"
                @click="changeInputType(type)"
                class="!badge !px-3 !py-2 !text-xs !rounded-md !flex !items-center !gap-1 !transition-all !duration-200 !ease-in-out hover:!scale-105"
                :class="type === localInputType ? '!bg-primary !text-white !shadow-md' : '!bg-gray-200 !text-gray-700 hover:!bg-gray-300'"
              >
                <!-- Text Icon -->
                <svg v-if="type === 'text'" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke-width="1.5" stroke="currentColor" class="size-3">
                  <path stroke-linecap="round" stroke-linejoin="round" d="M3.75 6.75h16.5M3.75 12h16.5m-16.5 5.25H12" />
                </svg>
                
                <!-- HTML Icon -->
                <svg v-if="type === 'html'" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke-width="1.5" stroke="currentColor" class="size-3">
                  <path stroke-linecap="round" stroke-linejoin="round" d="M17.25 6.75 22.5 12l-5.25 5.25m-10.5 0L1.5 12l5.25-5.25m7.5-3-4.5 16.5" />
                </svg>
                
                <!-- Markdown Icon
                <svg v-if="type === 'markdown'" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke-width="1.5" stroke="currentColor" class="size-3">
                  <path stroke-linecap="round" stroke-linejoin="round" d="M3.75 12h16.5m-16.5 3.75h16.5M3.75 19.5h16.5M5.625 4.5h12.75a1.875 1.875 0 0 1 0 3.75H5.625a1.875 1.875 0 1 1 0-3.75Z" />
                </svg> -->
                
                {{ type }}
              </button>
            </div>

            <span class="!text-xs !text-black !opacity-80 mt-2">Custom <b>Output rules</b> can be created from settings.</span>
          </ul>
        </div>
        
        
      </div>
      
      <div v-if="!showResult && !isGenerating" class="!mt-2">
        <!-- Smart Content -->
        <div class="!dropdown  !cursor-pointer !hover:scale-105 !transition-all !duration-300 !ease-in-out !tooltip !tooltip-left !tooltip-secondary" data-tip="Smart Content">
          <div tabindex="0" role="button" class="m-0 p-0 smart-detected !bg-transparent">
            <span class="circle" :class="smartContent === true ? '!bg-primary' : '!bg-gray-300'"></span> <b :class="smartContent === true ? '!text-primary' : '!text-gray-400'">{{ smartContent ? "Smart" : "Not Personalized" }}</b>
            <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke-width="1.5" stroke="currentColor" class="size-2 inline-block">
              <path stroke-linecap="round" stroke-linejoin="round" d="m19.5 8.25-7.5 7.5-7.5-7.5" />
            </svg>
          </div>
          <ul tabindex="0" class="dropdown-content menu bg-base-100 rounded-box z-1 w-52 !p-4 shadow-sm">
            
            <div class="flex flex-wrap gap-2">
              <button 
                @click="smartContent = !smartContent"
                class="!badge !px-3 !py-2 !text-xs !rounded-md !flex !items-center !gap-1 !transition-all !duration-200 !ease-in-out hover:!scale-105"
                :class="smartContent === false ? '!bg-primary !text-white' : '!bg-gray-200 !text-gray-700 hover:!bg-gray-300'"
              >
                <!-- Text Icon -->
                <svg v-if="type === 'text'" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke-width="1.5" stroke="currentColor" class="size-3">
                  <path stroke-linecap="round" stroke-linejoin="round" d="M3.75 6.75h16.5M3.75 12h16.5m-16.5 5.25H12" />
                </svg>
                
                <!-- HTML Icon -->
                <svg v-if="type === 'html'" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke-width="1.5" stroke="currentColor" class="size-3">
                  <path stroke-linecap="round" stroke-linejoin="round" d="M17.25 6.75 22.5 12l-5.25 5.25m-10.5 0L1.5 12l5.25-5.25m7.5-3-4.5 16.5" />
                </svg>
                
                <!-- Markdown Icon
                <svg v-if="type === 'markdown'" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke-width="1.5" stroke="currentColor" class="size-3">
                  <path stroke-linecap="round" stroke-linejoin="round" d="M3.75 12h16.5m-16.5 3.75h16.5M3.75 19.5h16.5M5.625 4.5h12.75a1.875 1.875 0 0 1 0 3.75H5.625a1.875 1.875 0 1 1 0-3.75Z" />
                </svg> -->
                
                {{ smartContent ? "Deactivate Smart Content" : "Activate Smart Content" }}
              </button>
            </div>

            <span class="!text-xs !text-black !opacity-80 mt-2"><b>Smart Content</b> Lets your <b>AI</b> know your <b>Brand</b> and <b>Tone</b>, you can control this from settings.</span>
          </ul>
        </div>

        <!-- Model Selector -->
        <div class="!dropdown !cursor-pointer !hover:scale-105 !transition-all !duration-300 !ease-in-out">
          <div tabindex="0" role="button" class="!text-content-secondary !text-xs m-0 p-0 !opacity-80">
            <span class="!text-content-secondary !flex !items-center !gap-1">{{ selectedModel.name }} 
              <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke-width="1.5" stroke="currentColor" class="size-3">
              <path stroke-linecap="round" stroke-linejoin="round" d="m19.5 8.25-7.5 7.5-7.5-7.5" />
              </svg>
            </span>
          </div>
          <ul tabindex="0" class="dropdown-content !menu !bg-base-100 !rounded-box z-1 !w-52 !p-4 !shadow-sm">
            <li v-for="(model, index) in availableModels" :key="index" 
                class="!text-xs hover:bg-secondary !rounded-md" 
                :class="{'!tooltip !tooltip-left !tooltip-secondary': model.tooltip}" 
                :data-tip="model.tooltip">
              <a @click="selectModel(model)" class="!text-black hover:!text-white">{{ model.name }}
                <span v-if="model.badge" 
                      class="rounded-md !absolute !right-2 !p-1 !text-[10px] text-white"
                      :class="model.badgeStyle === 'premium' ? 'bg-primary' : 'bg-secondary'">
                  {{ model.badge }}
                </span>
              </a>
            </li>
          </ul>
        </div>
        
      </div>
    </div>
    
    <div v-if="showResult" id="smart-result-content" class="smart-press-result" ref="contentContainer"  @scroll="handleManualScroll">
      <!-- button to copy and back -->
      <div class="textarea-controls !overflow-visible !overflow-y-visible">
       
        <button 
          class="control-btn relative !tooltip !tooltip-left" 
          data-tip="Back"
          @click="showResult = false"
        >
        <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke-width="1.5" stroke="currentColor" class="size-4">
            <path stroke-linecap="round" stroke-linejoin="round" d="M9 15 3 9m0 0 6-6M3 9h12a6 6 0 0 1 0 12h-3" />
          </svg>
        </button>
        <button 
          class="control-btn absolute right-0 !tooltip !tooltip-left" 
          data-tip="Copy to clipboard"
          @click.prevent="copyToClipboard(generatedContent, 'result')"
        >
          <span v-if="showResultCopiedMessage" class="copied-message">Copied!</span>
          <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke-width="1.5" stroke="currentColor" class="size-4">
            <path stroke-linecap="round" stroke-linejoin="round" d="M9 12h3.75M9 15h3.75M9 18h3.75m3 .75H18a2.25 2.25 0 0 0 2.25-2.25V6.108c0-1.135-.845-2.098-1.976-2.192a48.424 48.424 0 0 0-1.123-.08m-5.801 0c-.065.21-.1.433-.1.664 0 .414.336.75.75.75h4.5a.75.75 0 0 0 .75-.75 2.25 2.25 0 0 0-.1-.664m-5.8 0A2.251 2.251 0 0 1 13.5 2.25H15c1.012 0 1.867.668 2.15 1.586m-5.8 0c-.376.023-.75.05-1.124.08C9.095 4.01 8.25 4.973 8.25 6.108V8.25m0 0H4.875c-.621 0-1.125.504-1.125 1.125v11.25c0 .621.504 1.125 1.125 1.125h9.75c.621 0 1.125-.504 1.125-1.125V9.375c0-.621-.504-1.125-1.125-1.125H8.25ZM6.75 12h.008v.008H6.75V12Zm0 3h.008v.008H6.75V15Zm0 3h.008v.008H6.75V18Z" />
          </svg>
        </button>
      </div>
      
      <div v-html="renderContent()" class="!max-h-[100%] !text-black"></div>
    </div>
    
    <div v-if="showResult" class="smart-buttons !mt-2" id="smart-result-buttons">
      <!-- Re-Generate button -->
          <a href="#" class="smart-press-button flex items-center justify-center tooltip tooltip-left" data-tip="Re-Generate New Content" @click.prevent="generateContent">
            <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke-width="1.5" stroke="currentColor" class="size-6">
            <path stroke-linecap="round" stroke-linejoin="round" d="M19.5 12c0-1.232-.046-2.453-.138-3.662a4.006 4.006 0 0 0-3.7-3.7 48.678 48.678 0 0 0-7.324 0 4.006 4.006 0 0 0-3.7 3.7c-.017.22-.032.441-.046.662M19.5 12l3-3m-3 3-3-3m-12 3c0 1.232.046 2.453.138 3.662a4.006 4.006 0 0 0 3.7 3.7 48.656 48.656 0 0 0 7.324 0 4.006 4.006 0 0 0 3.7-3.7c.017-.22.032-.441.046-.662M4.5 12l3 3m-3-3-3 3" />
          </svg>

          </a>
        <div class="flex items-center justify-center gap-1">
          <!-- replace content -->
          <a href="#" class="!btn !btn-secondary !bg-gray-300 border-gray-500 shadow-none !text-black !text-xs !rounded-md !no-underline !font-normal hover:scale-105 transition-all duration-300 ease-in-out reset focus:!outline-none focus:!ring-0 tooltip tooltip-left" data-tip="Replace content" @click="replaceContent">
            <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke-width="1.5" stroke="currentColor" class="size-6">
              <path stroke-linecap="round" stroke-linejoin="round" d="M11.35 3.836c-.065.21-.1.433-.1.664 0 .414.336.75.75.75h4.5a.75.75 0 0 0 .75-.75 2.25 2.25 0 0 0-.1-.664m-5.8 0A2.251 2.251 0 0 1 13.5 2.25H15c1.012 0 1.867.668 2.15 1.586m-5.8 0c-.376.023-.75.05-1.124.08C9.095 4.01 8.25 4.973 8.25 6.108V8.25m8.9-4.414c.376.023.75.05 1.124.08 1.131.094 1.976 1.057 1.976 2.192V16.5A2.25 2.25 0 0 1 18 18.75h-2.25m-7.5-10.5H4.875c-.621 0-1.125.504-1.125 1.125v11.25c0 .621.504 1.125 1.125 1.125h9.75c.621 0 1.125-.504 1.125-1.125V18.75m-7.5-10.5h6.375c.621 0 1.125.504 1.125 1.125v9.375m-8.25-3 1.5 1.5 3-3.75" />
            </svg>

          </a>
        <!-- append content -->
          <a href="#" class="!btn !btn-secondary !bg-secondary !text-white !text-xs !rounded-md !no-underline !font-normal hover:scale-105 transition-all duration-300 ease-in-out reset focus:!outline-none focus:!ring-0 tooltip tooltip-left" data-tip="Append content" @click="appendContent">
            <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke-width="1.5" stroke="currentColor" class="size-6">
            <path stroke-linecap="round" stroke-linejoin="round" d="M3 4.5h14.25M3 9h9.75M3 13.5h5.25m5.25-.75L17.25 9m0 0L21 12.75M17.25 9v12" />
            </svg>

          </a>
        </div>
    </div>
  </div>
</template>

<script>
import axios from 'axios';
// import { DotLottieVue } from '@lottiefiles/dotlottie-vue'

export default {
  name: 'ContentGenerator',
  components: {
    // DotLottieVue
  },
  props: {
    selectedText: String,
    inputType: String,
    referenceSelected: Boolean,
    prompt: String,
    showTemplatesPanel: Boolean,
    showHistoryPanel: Boolean,
    customFields: Object,
    variablesPanel: Object
  },
  data() {
    return {
      localPrompt: this.prompt,
      localInputType: this.inputType || localStorage.getItem('smartPressInputType') || 'text',
      isGenerating: false,
      showResult: false,
      generatedContent: '',
      isTextareaExpanded: false,
      showPromptCopiedMessage: false,
      showResultCopiedMessage: false,
      isAutoScrolling: false,
      autoScrollEnabled: true,
      scrollAnimationId: null,
      historyType: 'prompts', // 'prompts' or 'content'
      contentHistory: [], // Array to store content history
      historyMaxLength: 10, // Default max history items
      selectedModel: null,
      isEnhancing: false,
      inputTypes: ['text', 'html'], // Array of available input types
      lastUsedPrompt: '', // Store the last used prompt for re-generation
      availableModels: [
        {
          name: 'Auto',
          tooltip: 'Auto-selects the best model based on your prompt and subscription plan.',
          badge: null,
          badgeStyle: null
        },
        {
          name: 'GPT-4o mini',
          tooltip: null,
          badge: 'Basic',
          badgeStyle: 'basic'
        },
        {
          name: 'Claude-3.7 Sonnet',
          tooltip: 'Best Performance for SEO Optimized Content Generation and text Humanization.',
          badge: 'Premium',
          badgeStyle: 'premium'
        },
        {
          name: 'Claude-3.5 Sonnet',
          tooltip: null,
          badge: null,
          badgeStyle: null
        },
        {
          name: 'Gemini Flash PRO',
          tooltip: null,
          badge: null,
          badgeStyle: null
        },
        {
          name: 'GPT-4o',
          tooltip: null,
          badge: null,
          badgeStyle: null
        },
        {
          name: 'GPT-4',
          tooltip: null,
          badge: null,
          badgeStyle: null
        },
        {
          name: 'Deepseek R1',
          tooltip: null,
          badge: null,
          badgeStyle: null
        }
      ],
      skeletonElements: [], // Array will be populated with objects that include a visible property
      smartContent: true,
    };
  },
  created() {
    // Initialize content history from localStorage
    this.contentHistory = JSON.parse(localStorage.getItem('smartPressContentHistory') || '[]');
    
    // Initialize with 'Auto' as the default selected model
    this.selectedModel = this.availableModels[0];
  },
  mounted() {
    // Check for any history preferences in localStorage
    const savedHistoryMaxLength = localStorage.getItem('smartPressHistoryMaxLength');
    if (savedHistoryMaxLength) {
      this.historyMaxLength = parseInt(savedHistoryMaxLength, 10);
    }
    
    // Emit the initial input type to parent component
    this.$emit('update:inputType', this.localInputType);
    
    // Generate initial random skeletons
    this.generateRandomSkeletons();
  },
  methods: {
    renderContent() {
      if (this.localInputType === 'html') {
        return this.generatedContent;
      } else if (this.localInputType === 'markdown') {
        // For markdown, we could potentially use a markdown library
        // For now, just convert basic line breaks for readability
        return this.generatedContent.replace(/\n+/g, '<br/>');
        // return this.generatedContent;
      } else {
        // Default text handling
        return this.generatedContent.replace(/\n+/g, '<br/>');
      }
    },
    changeInputType(type) {
      this.localInputType = type;
      // Save to localStorage
      localStorage.setItem('smartPressInputType', type);
      // Emit event to parent component to update the input type
      this.$emit('update:inputType', type);
    },
    enhancePrompt(){
      if (this.isEnhancing) return;
      this.isEnhancing = true;
      
      const originalPrompt = this.localPrompt;
      
      // First, make the API call to get the enhanced prompt
      axios.post('https://67c9c3d498a9fb9b5174.10g8.cloud/enhance', {
        session: this.customFields.api_key,
        prompt: originalPrompt,
        type: this.localInputType,
      }).then(response => {
        if(response.data.text){
        const enhancedPrompt = response.data.text;
        
        // Now that we have the enhanced text, start animations
        // Super fast animation speeds (to complete within 1 second total)
        const promptLength = originalPrompt.length;
        const enhancedLength = enhancedPrompt.length;
        // const totalChars = promptLength + enhancedLength;
        
        // Allocate time: 40% for deletion, 60% for typing
        const totalAnimationTime = 1000; // 1 second max
        const deleteSpeed = Math.max(1, Math.floor((totalAnimationTime * 0.4) / promptLength));
        const typeSpeed = Math.max(1, Math.floor((totalAnimationTime * 0.6) / enhancedLength));
        
        let tempPrompt = originalPrompt;
        
        // Animation to delete the existing prompt
        const deleteAnimation = () => {
          if (tempPrompt.length > 0) {
            tempPrompt = tempPrompt.slice(0, -1);
            this.localPrompt = tempPrompt;
            setTimeout(deleteAnimation, deleteSpeed);
          } else {
            // When deletion is complete, start typing the new enhanced prompt
            typeAnimation(enhancedPrompt);
          }
        };
        
        // Animation to type in the new prompt
        const typeAnimation = (text) => {
          let currentIndex = 0;
          tempPrompt = '';
          
          const typeNextChar = () => {
            if (currentIndex < text.length) {
              tempPrompt += text[currentIndex];
              this.localPrompt = tempPrompt;
              currentIndex++;
              setTimeout(typeNextChar, typeSpeed);
            } else {
              // Animation complete
              this.isEnhancing = false;
            }
          };
          
          // Start typing animation
          typeNextChar();
        };
        
        // Start the delete animation
        deleteAnimation();
      }else{
        this.isEnhancing = false;
        if(response.data.error === "Unauthorized"){
          alert("Unauthorized Access. Please Go to SmartPress Dashboard and update your account.");
        }
      }
        
      }).catch(error => {
        console.error('Error enhancing prompt:', error);
        this.isEnhancing = false;
      });
    },
    toggleTextareaExpand() {
      this.isTextareaExpanded = !this.isTextareaExpanded;
    },
    generateRandomSkeletons() {
      // Heights options with more consistent sizing
      const smallHeights = ['h-2', 'h-3', 'h-4'];
      const mediumHeights = ['h-5', 'h-6', 'h-7'];
      const largeHeights = ['h-16', 'h-20', 'h-24'];
      
      // Width options for better consistency
      const narrowWidths = ['w-24', 'w-32'];
      const mediumWidths = ['w-40', 'w-48'];
      const fullWidth = 'w-full';
      
      // Create a structured layout that resembles content
      this.skeletonElements = [
        // First element - always large (like a heading or feature image)
        {
          height: largeHeights[Math.floor(Math.random() * largeHeights.length)],
          width: Math.random() > 0.2 ? fullWidth : mediumWidths[1],
          rounded: true,
          visible: false
        },
        // Second element - always a full-width small element (like a subtitle)
        {
          height: smallHeights[Math.floor(Math.random() * smallHeights.length)],
          width: fullWidth,
          rounded: false, 
          visible: false
        },
        // Third element - medium height (like a paragraph)
        {
          height: mediumHeights[Math.floor(Math.random() * mediumHeights.length)],
          width: fullWidth,
          rounded: false,
          visible: false
        },
        // Fourth to eighth elements - mix of sizes for text blocks
        ...Array.from({ length: 5 }, () => {
          const isFullWidth = Math.random() > 0.3;
          return {
            height: smallHeights[Math.floor(Math.random() * smallHeights.length)],
            width: isFullWidth ? fullWidth : 
                  (Math.random() > 0.5 ? mediumWidths[Math.floor(Math.random() * mediumWidths.length)] : 
                                        narrowWidths[Math.floor(Math.random() * narrowWidths.length)]),
            rounded: false,
            visible: false
          };
        })
      ];
      
      // Animate elements appearance with randomized delay
      this.animateSkeletonAppearance();
    },
    animateSkeletonAppearance() {
      // Make elements appear one by one with random delays between a range
      this.skeletonElements.forEach((skeleton, index) => {
        // Generate a random delay between 80ms and 200ms for each element
        // First element appears quickly, subsequent elements have progressively increasing base delay
        const minDelay = 350 + (index * 300);
        const maxDelay = 1200 + (index * 800);
        const randomDelay = Math.floor(Math.random() * (maxDelay - minDelay) + minDelay);
        
        setTimeout(() => {
          this.skeletonElements[index].visible = true;
        }, randomDelay);
      });
    },
    async generateContent() {
      this.isGenerating = true;
      // Generate random skeletons every time content generation starts
      this.generateRandomSkeletons();
      
      // Close panels
      this.$emit('close-templates', false);
      this.$emit('close-history', false);
      this.$emit('close-variables', false);
      this.$emit('toggle-generating', true);
      this.showResult = false;
      this.generatedContent = '';
      
      let promptToUse = this.localPrompt;
      // If current prompt is empty but we have a last used prompt, use that
      if ((this.localPrompt.length < 2) && localStorage.getItem('smartPressLastUsedPrompt')) {
      //  Regenerate with last used prompt
        promptToUse = localStorage.getItem('smartPressLastUsedPrompt');
      }else{
        // Notify parent to save prompt to history
       this.$emit('save-prompt', this.localPrompt);
        // Save the current prompt as the last used prompt
      this.lastUsedPrompt = promptToUse;
      localStorage.setItem('smartPressLastUsedPrompt', promptToUse);
      }
      
      
      
      
      // If variables panel exists and has processed content, use that
      if (this.variablesPanel && typeof this.variablesPanel.getProcessedPrompt === 'function') {
        if(this.variablesPanel.getProcessedPrompt().length > 2){
        promptToUse = this.variablesPanel.getProcessedPrompt();
        // remove variables from prompt
        promptToUse = promptToUse.replace(/{{[^}]*}}/g, '');
        }
      }
      
      if(this.selectedText.length > 2 && this.referenceSelected){
        promptToUse = promptToUse + " Reference text: " + this.selectedText;
      }
      
      if(this.customFields.api_key) {
        try {
          const body = {
            session: this.customFields.api_key,
            prompt: promptToUse,
            reference_selected: this.referenceSelected,
            model: this.selectedModel.name == 'Auto' ? "basic" : this.selectedModel.name, // Pass the selected model name to the API
            type: this.localInputType,
          };
          if(this.customFields.brand_data && this.smartContent){
            body.brand_data = this.customFields.brand_data;
          }
          const response = await axios.post('https://67c9c3d498a9fb9b5174.10g8.cloud/generate-text', body);
          
          if(response.data.success || response.data.text){
            // Content Generated Successfully
          this.generatedContent = response.data.text;
          this.showResult = true;

          // Save generated content to history
          this.saveContentToHistory(this.generatedContent);
          
          this.$emit('update:prompt', '');
          localStorage.setItem('smartPressPrompt', '');

          }else{
            // Error Generating Content
            this.generatedContent = response.data.error;
            if(response.data.error === "Unauthorized"){
              this.generatedContent = "Unauthorized Access. Please <a href='/wp-admin/admin.php?page=smart-press' class='!text-primary'>Go to SmartPress Dashboard</a> and update your account.";
            }
            this.showResult = true;
          }
          
        } catch (error) {
          console.error('Error generating content:', error);
          // Handle error - maybe show a message to user
        } finally {
          this.isGenerating = false;
        }
      }else{
        this.generatedContent = "No User Found. Please <a href='/wp-admin/admin.php?page=smart-press' class='!text-primary'>Go to SmartPress Dashboard</a> and update your account.";
        this.showResult = true;
        this.isGenerating = false;
      }
    },
    appendContent() {
      this.$emit('append-content', this.generatedContent);
    },
    replaceContent() {
      this.$emit('replace-content', this.generatedContent);
    },
    async copyToClipboard(content, type = 'prompt') {
      try {
        await navigator.clipboard.writeText(content);
        if (type === 'prompt') {
          this.showPromptCopiedMessage = true;
          setTimeout(() => {
            this.showPromptCopiedMessage = false;
          }, 2000);
        } else {
          this.showResultCopiedMessage = true;
          setTimeout(() => {
            this.showResultCopiedMessage = false;
          }, 2000);
        }
      } catch (err) {
        console.error('Failed to copy text: ', err);
      }
    },
    handleManualScroll() {
      if (!this.isAutoScrolling) {
        this.autoScrollEnabled = false;
      }
    },
    async autoScrollContent() {
      if (!this.autoScrollEnabled || !this.$refs.contentContainer) return;

      const container = this.$refs.contentContainer;
      const scrollHeight = container.scrollHeight;
      const duration = 3000; // 3 seconds to scroll to bottom
      const startTime = performance.now();
      const startPosition = container.scrollTop;

      const animate = (currentTime) => {
        if (!this.autoScrollEnabled) {
          cancelAnimationFrame(this.scrollAnimationId);
          return;
        }

        const elapsed = currentTime - startTime;
        const progress = Math.min(elapsed / duration, 1);

        this.isAutoScrolling = true;
        container.scrollTop = startPosition + (scrollHeight - container.clientHeight) * progress;
        
        if (progress < 1) {
          this.scrollAnimationId = requestAnimationFrame(animate);
        } else {
          this.resetAutoScroll();
        }
      };

      this.scrollAnimationId = requestAnimationFrame(animate);
    },
    resetAutoScroll() {
      this.autoScrollEnabled = true;
      
      if (this.$refs.contentContainer) {
        const container = this.$refs.contentContainer;
        const startPosition = container.scrollTop;
        const duration = 500; // Fast animation (0.5 seconds)
        const startTime = performance.now();
        
        const animateToTop = (currentTime) => {
          const elapsed = currentTime - startTime;
          const progress = Math.min(elapsed / duration, 1);
          
          this.isAutoScrolling = true;
          const easeProgress = 1 - (1 - progress) * (1 - progress);
          container.scrollTop = startPosition * (1 - easeProgress);
          
          if (progress < 1) {
            requestAnimationFrame(animateToTop);
          } else {
            this.isAutoScrolling = false;
          }
        };
        
        requestAnimationFrame(animateToTop);
      }
    },
    // New method to save content to history
    saveContentToHistory(content) {
      // Save locally
      this.saveToHistory(content, 'content');
      
      // Emit event for parent component
      this.$emit('save-content-history', content);
    },
    // Generic method to handle both prompt and content history
    saveToHistory(item, type) {
      const historyKey = type === 'prompts' ? 'smartPressPromptHistory' : 'smartPressContentHistory';
      
      // Get existing history
      let history = JSON.parse(localStorage.getItem(historyKey) || '[]');
      
      // Add new item at the beginning (most recent first)
      history.unshift({
        text: item,
        timestamp: new Date().toISOString()
      });
      
      // Limit history length
      history = history.slice(0, this.historyMaxLength);
      
      // Save back to localStorage
      localStorage.setItem(historyKey, JSON.stringify(history));
      
      // Update local array if it's content history
      if (type === 'content') {
        this.contentHistory = history;
      }
    },
    // Method to toggle between prompt and content history
    toggleHistoryType() {
      this.historyType = this.historyType === 'prompts' ? 'content' : 'prompts';
      // Emit event to notify parent component about the history type change
      this.$emit('toggle-history-type', this.historyType);
    },
    // Method to set history max length
    setHistoryMaxLength(length) {
      if (typeof length === 'number' && length > 0) {
        this.historyMaxLength = length;
        // You might want to trim existing history to match new length
        this.trimHistoryToMaxLength();
      }
    },
    // Method to trim history to max length
    trimHistoryToMaxLength() {
      // Trim prompts history
      let promptsHistory = JSON.parse(localStorage.getItem('smartPressPromptHistory') || '[]');
      promptsHistory = promptsHistory.slice(0, this.historyMaxLength);
      localStorage.setItem('smartPressPromptHistory', JSON.stringify(promptsHistory));
      
      // Trim content history
      let contentHistory = JSON.parse(localStorage.getItem('smartPressContentHistory') || '[]');
      contentHistory = contentHistory.slice(0, this.historyMaxLength);
      localStorage.setItem('smartPressContentHistory', JSON.stringify(contentHistory));
      this.contentHistory = contentHistory;
    },
    selectModel(model) {
      this.selectedModel = model;
      // You might want to emit an event to notify parent components
      this.$emit('model-selected', model);
    }
  },
  watch: {
    prompt(newVal) {
      this.localPrompt = newVal;
    },
    inputType(newVal) {
      this.localInputType = newVal;
    },
    localPrompt(newVal) {
      this.$emit('update:prompt', newVal);
      localStorage.setItem('smartPressPrompt', newVal);
    },
    generatedContent: {
      handler(newVal) {
        if (newVal) {
          this.$nextTick(() => {
            this.resetAutoScroll();
            setTimeout(() => {
              this.autoScrollContent();
            }, 500);
          });
        }
      }
    },
    historyMaxLength(newValue) {
      localStorage.setItem('smartPressHistoryMaxLength', newValue.toString());
    },
    historyType(newValue) {
      localStorage.setItem('smartPressHistoryType', newValue);
    }
  }
}
</script>

<style scoped>
.smart-generating-content {
  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: center;
  height: 100%;
  width: 100%;
}

.textarea-container {
  position: relative;
  width: 100%;
}

.textarea-controls {
  position: absolute;
  top: 8px;
  right: 8px;
  display: flex;
  gap: 8px;
  z-index: 1;
}

.control-btn {
  position: relative;
  background: rgba(255, 255, 255, 0.8);
  border: none;
  border-radius: 4px;
  width: 28px;
  height: 28px;
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  transition: background-color 0.2s;
  color: #333;
  z-index: 2;
}

.control-btn:hover {
  background: rgba(255, 255, 255, 1);
}

.textarea-container.expanded {
  position: fixed;
  top: 10%;
  left: 10%;
  width: 50vw;
  min-height: 300px;
  max-height: 50vh;
  z-index: 10001;
  background: white;
  box-shadow: 0 5px 20px rgba(0, 0, 0, 0.3);
  border-radius: 8px;
}

.textarea-container.expanded .smart-press-textarea {
  width: 100%;
  height: 100%;
  min-height: 300px;
  max-height: 50vh;
  padding: 20px;
  resize: none;
  font-size: 16px;
}

.smart-press-textarea {
  transition: all 0.3s ease;
}

#smart-edit-container.has-expanded-textarea .textarea-container:not(.expanded) {
  visibility: hidden;
  opacity: 0;
}

.textarea-container {
  transition: all 0.3s ease-in-out;
}

.smart-press-secondary-button.active {
  background-color: #e0e0e0;
}

.smart-press-result {
  position: relative;
  padding-top: 40px; /* Add some space for the copy button */
}

.fixed-top-right {
  position: absolute !important;
  top: 8px !important;
  right: 8px !important;
}

.copied-message {
  position: absolute;
  bottom: -25px;
  left: 50%;
  transform: translateX(-50%);
  background-color: #10B981;
  color: white;
  padding: 4px 8px;
  border-radius: 4px;
  font-size: 12px;
  white-space: nowrap;
  animation: fadeInOut 2s ease-in-out;
  z-index: 3;
  pointer-events: none;
}

@keyframes fadeInOut {
  0% { opacity: 0; transform: translate(-50%, 5px); }
  15% { opacity: 1; transform: translate(-50%, 0); }
  85% { opacity: 1; transform: translate(-50%, 0); }
  100% { opacity: 0; transform: translate(-50%, -5px); }
}

.smart-press-result div {
  max-height: 500px;
  overflow-y: auto;
  scroll-behavior: smooth;
}

.skeleton-container {
  width: 90%;
  max-width: 480px;
  min-height: 200px;
  max-height: 350px;
  overflow: hidden;
  position: relative;
  display: flex;
  align-items: flex-start;
}

.skeleton-item {
  transition: all 0.3s ease;
  margin-bottom: 8px;
}

.skeleton-fade-enter-from {
  opacity: 0;
  transform: translateY(8px);
}

.skeleton-fade-enter-active {
  transition: all 0.4s cubic-bezier(0.2, 0.8, 0.2, 1);
}

.skeleton-fade-enter-to {
  opacity: 1;
  transform: translateY(0);
}
</style> 